import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import WhatsAppButton from './WhatsappButton';
import { ReactComponent as KairosLogo } from '../imgs/kairos_white_letters.svg';

const AboutUs: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(99, 102, 241, 0.2), transparent 70%)', filter: 'blur(60px)' }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span
              style={{
                background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {t.about.title}
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t.about.subtitle}
          </p>
        </motion.div>

        <WhatsAppButton />

        {/* Developer and Partner Side by Side */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {/* Developer Profile */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="glass p-8 rounded-2xl h-full flex flex-col" style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)' }}>
                  <span className="text-3xl">💻</span>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">{t.about.developer.title}</h2>
                </div>
              </div>

              <p className="text-lg text-gray-300 mb-8 leading-relaxed flex-grow">
                {t.about.developer.description}
              </p>

              {/* Portfolio Link with Preview */}
              <div className="space-y-4">
                <a
                  href="https://dev.m-kaos.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)', border: '1px solid rgba(99, 102, 241, 0.3)' }}
                >
                  <div className="flex items-center gap-3 p-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-primary)' }}>
                      <span className="text-xl">⚡</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white group-hover:text-[var(--color-primary)] transition-colors">{t.about.developer.devPortfolio}</h3>
                      <p className="text-sm text-gray-400">dev.m-kaos.com</p>
                    </div>
                    <span className="text-gray-400 group-hover:text-[var(--color-primary)] transition-colors">↗️</span>
                  </div>

                  {/* Website Preview Thumbnail */}
                  <div className="relative w-full h-48 overflow-hidden border-t" style={{ borderColor: 'rgba(99, 102, 241, 0.3)' }}>
                    <iframe
                      src="https://dev.m-kaos.com"
                      className="w-full h-[600px] pointer-events-none scale-[0.333] origin-top-left"
                      style={{
                        width: '300%',
                        transform: 'scale(0.333)',
                        transformOrigin: 'top left'
                      }}
                      title="Developer Portfolio Preview"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
                  </div>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Partner Company */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="glass p-8 rounded-2xl h-full flex flex-col" style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-primary) 100%)' }}>
                  <span className="text-3xl">📢</span>
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">{t.about.partner.title}</h2>
                </div>
              </div>

              <p className="text-lg text-gray-300 mb-8 leading-relaxed flex-grow">
                {t.about.partner.description}
              </p>

              {/* Partner Link with Preview */}
              <div className="space-y-4">
                <a
                  href="https://harustudio.com.mx/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)', border: '1px solid rgba(236, 72, 153, 0.3)' }}
                >
                  <div className="flex items-center gap-3 p-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent)' }}>
                      <span className="text-xl">🎨</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white group-hover:text-[var(--color-accent)] transition-colors">{t.about.partner.company}</h3>
                      <p className="text-sm text-gray-400">harustudio.com.mx</p>
                    </div>
                    <span className="text-gray-400 group-hover:text-[var(--color-accent)] transition-colors">↗️</span>
                  </div>

                  {/* Website Preview Thumbnail */}
                  <div className="relative w-full h-48 overflow-hidden border-t" style={{ borderColor: 'rgba(236, 72, 153, 0.3)' }}>
                    <iframe
                      src="https://harustudio.com.mx/"
                      className="w-full h-[600px] pointer-events-none scale-[0.333] origin-top-left"
                      style={{
                        width: '300%',
                        transform: 'scale(0.333)',
                        transformOrigin: 'top left'
                      }}
                      title="Haru Studio Preview"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
                  </div>
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="max-w-4xl mx-auto p-8 rounded-2xl" style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
            <div className="flex justify-center mb-6">
              <div className="h-12 overflow-hidden">
                <KairosLogo
                  className="h-full w-auto"
                  style={{
                    fill: 'url(#mission-logo-gradient)',
                    transform: 'scale(1.5)',
                    transformOrigin: 'center'
                  }}
                />
                <svg width="0" height="0">
                  <defs>
                    <linearGradient id="mission-logo-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{ stopColor: 'var(--color-primary)', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: 'var(--color-accent)', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
            <p className="text-lg text-gray-300 leading-relaxed">
              At Kairos, we believe that every business deserves access to powerful automation tools that can transform
              their operations. Our mission is to eliminate repetitive tasks, reduce operational costs, and empower teams
              to focus on strategic growth. We're not just building software—we're building partnerships that drive success.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutUs;
